import { useCallback, useMemo, useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import {
  CARRIERS,
  PDF_ACCEPTED_EXTENSIONS,
  PDF_UPLOAD_MAX_BYTES,
  TERMINALS,
  isValidContainerNumber,
  normalizeContainerNumber,
  resolveCarrier,
  resolveCarrierAlias,
  type ImportTrackInput,
  type ImportTrackResult,
  type PdfImportResult,
  type PdfImportRow,
} from '@ctp/shared';
import { ApiError, importApi } from '../lib/api';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Input,
  Modal,
  Spinner,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from './ui';

/** Eine Zeile im Bearbeiten-Schritt (der Nutzer darf jedes Feld ändern). */
interface EditRow {
  /** Stabiler Schlüssel für React (Nummern dürfen sich beim Editieren ändern/doppeln). */
  key: string;
  containerNumber: string;
  carrierInput: string;
  /** Der Nutzer hat die Reederei selbst gesetzt → nicht mehr automatisch überschreiben. */
  carrierTouched: boolean;
  terminalId: string;
  /** Herkunft der Zeile (KI/Dokument) — nur informativ. */
  origin: PdfImportRow['source'] | 'manual';
  page?: number;
  context?: string;
}

type Step = 'upload' | 'review' | 'result';

let rowCounter = 0;
const nextKey = (): string => `pdf-row-${++rowCounter}`;

/** Reederei-Freitext → kanonischer Code (Alias/Name/Code), sonst Großschreibung des Rohwerts. */
function manualCarrierCode(input: string): string | undefined {
  const trimmed = input.trim();
  if (!trimmed) return undefined;
  return resolveCarrierAlias(trimmed)?.code ?? trimmed.toUpperCase();
}

/** Vorschau-Zeile → Bearbeiten-Zeile. */
function toEditRow(row: PdfImportRow): EditRow {
  return {
    key: nextKey(),
    containerNumber: row.containerNumber,
    carrierInput: row.carrierCode ?? '',
    carrierTouched: false,
    terminalId: row.terminalId ?? '',
    origin: row.source,
    ...(row.page !== undefined ? { page: row.page } : {}),
    ...(row.context ? { context: row.context } : {}),
  };
}

/**
 * Dashboard-PDF-Import. Der Nutzer lädt ein Transportdokument (Bill of Lading, Buchungsbestätigung,
 * Packliste) hoch; das System liest Container-Nummer, Reederei und — falls genannt — das Terminal
 * aus. Anschließend kann JEDE Zeile korrigiert, ergänzt oder gelöscht und eine Zeile von Hand
 * hinzugefügt werden. Gespeichert wird über denselben Lite-Pfad wie der Excel-Import; jede bediente
 * Abfrage zählt gegen Trial/Limit.
 */
export function PdfImportDialog({
  open,
  onClose,
  onImported,
  token,
}: {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  token: string | null;
}): JSX.Element {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [parsed, setParsed] = useState<PdfImportResult | null>(null);
  const [rows, setRows] = useState<EditRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportTrackResult | null>(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const reset = useCallback((): void => {
    setStep('upload');
    setParsed(null);
    setRows([]);
    setBusy(false);
    setProgress(0);
    setImporting(false);
    setResult(null);
    setError('');
    setDragOver(false);
  }, []);

  /** Schließen: nach erfolgtem Import das Dashboard aktualisieren lassen. */
  const handleClose = useCallback((): void => {
    if (result) onImported();
    reset();
    onClose();
  }, [result, onImported, onClose, reset]);

  /** Zeilenstatus: gültige Nummer? Dublette? speicherbar (Reederei ODER Terminal)? */
  const state = useMemo(() => {
    const seen = new Set<string>();
    return rows.map((r) => {
      const normalized = normalizeContainerNumber(r.containerNumber);
      const valid = isValidContainerNumber(normalized);
      const duplicate = valid && seen.has(normalized);
      if (valid) seen.add(normalized);
      const carrierCode = manualCarrierCode(r.carrierInput);
      return {
        normalized,
        valid,
        duplicate,
        carrierCode,
        trackable: valid && !duplicate && (!!carrierCode || !!r.terminalId),
      };
    });
  }, [rows]);

  const trackInputs = useMemo<ImportTrackInput[]>(
    () =>
      rows.flatMap((row, i) => {
        const s = state[i];
        if (!s?.trackable) return [];
        return [
          {
            containerNumber: s.normalized,
            ...(s.carrierCode ? { carrierCode: s.carrierCode } : {}),
            ...(row.terminalId ? { terminal: row.terminalId } : {}),
          },
        ];
      }),
    [rows, state],
  );

  const notTrackable = rows.length - trackInputs.length;

  function updateRow(index: number, patch: Partial<EditRow>): void {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function removeRow(index: number): void {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  /** Neue Zeile: Reederei/Terminal aus der Dokumenterkennung vorbelegen (spart Tipparbeit). */
  function addRow(): void {
    setRows((prev) => [
      ...prev,
      {
        key: nextKey(),
        containerNumber: '',
        carrierInput: parsed?.detection.carrierCode ?? '',
        carrierTouched: false,
        terminalId: parsed?.detection.terminalId ?? '',
        origin: 'manual',
      },
    ]);
  }

  /**
   * Container-Nummer geändert → Reederei aus dem Präfix nachziehen (verlässlichste Quelle, wie im
   * Parser). Eine vom Nutzer selbst eingetragene Reederei bleibt unangetastet.
   */
  function editNumber(index: number, value: string): void {
    const row = rows[index];
    if (!row) return;
    const patch: Partial<EditRow> = { containerNumber: value.toUpperCase() };
    if (!row.carrierTouched) {
      const res = resolveCarrier(normalizeContainerNumber(value));
      if (res.status === 'resolved') patch.carrierInput = res.carrier.code;
    }
    updateRow(index, patch);
  }

  function validateClientSide(file: File): string | null {
    const lower = file.name.toLowerCase();
    if (!PDF_ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext))) return t('pdfImport.errType');
    if (file.size > PDF_UPLOAD_MAX_BYTES)
      return t('pdfImport.errTooLarge', { max: Math.round(PDF_UPLOAD_MAX_BYTES / (1024 * 1024)) });
    return null;
  }

  async function handleFile(file: File): Promise<void> {
    setError('');
    const clientErr = validateClientSide(file);
    if (clientErr) {
      setError(clientErr);
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      const res = await importApi.parsePdf(file, token, setProgress);
      setParsed(res);
      setRows(res.rows.map(toEditRow));
      setStep('review');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('pdfImport.errUpload'));
    } finally {
      setBusy(false);
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }

  async function doImport(): Promise<void> {
    if (trackInputs.length === 0) return;
    setImporting(true);
    setError('');
    try {
      const res = await importApi.track(trackInputs, token);
      setResult(res);
      setStep('result');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('pdfImport.errImport'));
    } finally {
      setImporting(false);
    }
  }

  // ── Render-Bausteine ───────────────────────────────────────────────────────────────────
  function statusCell(index: number): JSX.Element {
    const s = state[index];
    const row = rows[index];
    if (!s || !row) return <span />;
    if (!row.containerNumber.trim())
      return <Badge variant="warning">{t('pdfImport.status.empty')}</Badge>;
    if (!s.valid) return <Badge variant="danger">{t('pdfImport.status.invalid')}</Badge>;
    if (s.duplicate) return <Badge variant="warning">{t('pdfImport.status.duplicate')}</Badge>;
    if (s.carrierCode) return <Badge variant="success">{t('pdfImport.status.trackable')}</Badge>;
    if (row.terminalId) return <Badge variant="primary">{t('pdfImport.status.terminalOnly')}</Badge>;
    return <Badge variant="warning">{t('pdfImport.status.needsSource')}</Badge>;
  }

  const title =
    step === 'result' ? t('pdfImport.resultTitle') : t('pdfImport.title');

  // ── Footer je Schritt ──────────────────────────────────────────────────────────────────
  let footer: JSX.Element;
  if (step === 'review') {
    footer = (
      <>
        <span className="mr-auto text-sm text-muted-foreground">
          {t('pdfImport.selectedCount', { count: trackInputs.length })}
          {notTrackable > 0 && ` · ${t('pdfImport.skippedCount', { count: notTrackable })}`}
        </span>
        <Button variant="secondary" onClick={handleClose} disabled={importing}>
          {t('pdfImport.cancel')}
        </Button>
        <Button onClick={() => void doImport()} disabled={importing || trackInputs.length === 0}>
          {importing ? <Spinner /> : t('pdfImport.importNow')}
        </Button>
      </>
    );
  } else if (step === 'result') {
    footer = <Button onClick={handleClose}>{t('pdfImport.done')}</Button>;
  } else {
    footer = (
      <Button variant="secondary" onClick={handleClose}>
        {t('pdfImport.cancel')}
      </Button>
    );
  }

  return (
    <Modal open={open} onClose={handleClose} title={title} footer={footer} size="xl">
      {error && (
        <Alert kind="error" className="mb-4">
          {error}
        </Alert>
      )}

      {/* ── Upload ─────────────────────────────────────────────────────── */}
      {step === 'upload' && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => fileInput.current?.click()}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && fileInput.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-12 text-center transition-colors ${
            dragOver ? 'border-primary bg-primary/5' : 'border-input hover:border-primary/50'
          }`}
        >
          {busy ? (
            <>
              <Spinner className="h-6 w-6" />
              <p className="text-sm text-muted-foreground">{t('pdfImport.analyzing')}</p>
              <div className="mt-2 h-2 w-56 overflow-hidden rounded-full bg-muted">
                <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
              </div>
            </>
          ) : (
            <>
              <p className="font-medium">{t('pdfImport.dropzone')}</p>
              <p className="max-w-md text-sm text-muted-foreground">{t('pdfImport.dropzoneHint')}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {PDF_ACCEPTED_EXTENSIONS.join(', ')} ·{' '}
                {t('pdfImport.maxSize', { max: Math.round(PDF_UPLOAD_MAX_BYTES / (1024 * 1024)) })}
              </p>
            </>
          )}
          <input
            ref={fileInput}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
              e.target.value = '';
            }}
          />
        </div>
      )}

      {/* ── Bearbeiten ─────────────────────────────────────────────────── */}
      {step === 'review' && parsed && (
        <div className="space-y-4">
          <div className="rounded-md border bg-muted/30 p-3 text-sm">
            <p className="font-medium">
              {parsed.filename}
              <span className="ml-2 font-normal text-muted-foreground">
                {t('pdfImport.pages', { count: parsed.detection.pageCount })}
              </span>
            </p>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {parsed.detection.carrierCode ? (
                <Badge variant="primary">
                  {t('pdfImport.detectedCarrier', {
                    carrier: parsed.detection.carrierName ?? parsed.detection.carrierCode,
                  })}
                </Badge>
              ) : (
                <Badge variant="default">{t('pdfImport.noCarrier')}</Badge>
              )}
              {parsed.detection.terminalId ? (
                <Badge variant="success">
                  {t('pdfImport.detectedTerminal', { terminal: parsed.detection.terminalLabel })}
                </Badge>
              ) : (
                <Badge variant="default">{t('pdfImport.noTerminal')}</Badge>
              )}
              {parsed.aiUsed && <Badge variant="purple">{t('pdfImport.aiUsed')}</Badge>}
            </div>
            <p className="mt-2 text-muted-foreground">{t('pdfImport.editHint')}</p>
          </div>

          {!parsed.textFound && <Alert kind="warn">{t('pdfImport.noTextLayer')}</Alert>}

          <datalist id="ctp-pdf-carrier-options">
            {CARRIERS.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </datalist>

          {rows.length === 0 ? (
            <EmptyState title={t('pdfImport.emptyTitle')} hint={t('pdfImport.emptyHint')} />
          ) : (
            <div className="max-h-[45vh] overflow-y-auto rounded-md border">
              <Table>
                <THead>
                  <TR>
                    <TH>{t('pdfImport.col.number')}</TH>
                    <TH>{t('pdfImport.col.carrier')}</TH>
                    <TH>{t('pdfImport.col.terminal')}</TH>
                    <TH>{t('pdfImport.col.status')}</TH>
                    <TH className="w-10" />
                  </TR>
                </THead>
                <TBody>
                  {rows.map((row, i) => (
                    <TR key={row.key}>
                      <TD>
                        <Input
                          value={row.containerNumber}
                          onChange={(e) => editNumber(i, e.target.value)}
                          placeholder={t('pdfImport.numberPlaceholder')}
                          aria-label={t('pdfImport.col.number')}
                          className="w-40 font-mono"
                        />
                        {row.context && (
                          <p className="mt-1 max-w-[16rem] truncate text-xs text-muted-foreground" title={row.context}>
                            {row.page ? `S. ${row.page}: ` : ''}
                            {row.context}
                          </p>
                        )}
                      </TD>
                      <TD>
                        <Input
                          list="ctp-pdf-carrier-options"
                          value={row.carrierInput}
                          onChange={(e) =>
                            updateRow(i, { carrierInput: e.target.value, carrierTouched: true })
                          }
                          placeholder={t('pdfImport.carrierPlaceholder')}
                          aria-label={t('pdfImport.col.carrier')}
                          className="w-40"
                        />
                      </TD>
                      <TD>
                        <select
                          value={row.terminalId}
                          onChange={(e) => updateRow(i, { terminalId: e.target.value })}
                          aria-label={t('pdfImport.col.terminal')}
                          className="h-9 w-52 rounded-md border border-input bg-background px-2 text-sm"
                        >
                          <option value="">{t('pdfImport.noTerminalOption')}</option>
                          {TERMINALS.map((term) => (
                            <option key={term.id} value={term.id}>
                              {term.label}
                            </option>
                          ))}
                        </select>
                      </TD>
                      <TD>{statusCell(i)}</TD>
                      <TD>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeRow(i)}
                          aria-label={t('pdfImport.removeRow')}
                          title={t('pdfImport.removeRow')}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          )}

          <Button variant="secondary" onClick={addRow}>
            <Plus className="h-4 w-4" />
            {t('pdfImport.addRow')}
          </Button>
        </div>
      )}

      {/* ── Ergebnis ───────────────────────────────────────────────────── */}
      {step === 'result' && result && (
        <div className="space-y-4">
          <p className="text-sm">
            {t('pdfImport.resultSummary', {
              tracked: result.summary.tracked,
              terminalOnly: result.summary.terminalOnly,
              skipped: result.summary.skipped,
            })}
          </p>
          {result.summary.limitReached && <Alert kind="warn">{t('pdfImport.limitReached')}</Alert>}
        </div>
      )}
    </Modal>
  );
}
